import {
	clientConfig, describeClient, describeClientFile, downloadBlob,
	exportFilename, formatDate, formatRange, hasLetterhead,
} from "./exportHelpers";
import {
	appendPages, createMergeContext, createTextPageWriter, embedLogo,
	fetchPdf,
} from "./pdfMerge";

/**
 * Generate and download one PDF containing the selected Kinderakte
 * documents: a head sheet with the letterhead and the overall
 * information, an index of all documents, then every loadable
 * document's pages -- each optionally preceded by a cover sheet with
 * the document's metadata. Built entirely with pdf-lib (there is no
 * jsPDF part; the uploaded documents make up almost all pages).
 *
 * options:
 *   client            : Parse Client (focused client)
 *   clientFiles       : ClientFile[] already filtered and sorted
 *   from, to          : Date bounds of the export (may be undefined)
 *   categoryNames     : names of the selected filter categories
 *   exportedBy        : full name of the exporting user
 *   includeCoverSheets: put a metadata cover sheet before every
 *                       document
 */
export const printRecordPdf = async (options) => {
	const { client, clientFiles, from, to } = options;
	const documents = clientFiles.map(describeClientFile);
	const context = await createMergeContext();
	const writer = createTextPageWriter(context);

	await addHeadSheet(context, writer, options);

	const results = [];
	for (const document of documents) {
		results.push({
			document,
			source: document.url ? await fetchPdf(document.url) : undefined,
		});
	}
	addDocumentIndex(writer, results);

	for (const result of results) {
		if (!result.source) { continue; }
		if (options.includeCoverSheets) {
			addCoverSheet(writer, result.document);
		}
		await appendPages(context, result.source);
	}

	const bytes = await context.merged.save();
	downloadBlob(exportFilename(client, from, to, "pdf", "Kinderakte"),
		new Blob([ bytes ], { type: "application/pdf" }));
};

async function addHeadSheet(context, writer, options) {
	const { client, clientFiles, from, to, categoryNames, exportedBy } =
		options;
	const info = describeClient(client);

	writer.newPage();
	// No configured letterhead: no address, no logo (never another
	// customer's branding); the head sheet starts with the heading.
	if (hasLetterhead()) {
		// Classic layout, fixed line slots at 15pt like the diary
		// export: the bold address block ends on slot 115 and the
		// contact block on slot 180, so shorter blocks bottom-align
		// against the heading (slot 200). The logo box spans
		// 357..525 x 50..206, its right edge on the text margin; the
		// blobs in client_config.json are padded for exactly this box.
		const address =
			[ "adress_titel", "adress_titel_zusatz", "adress_strasse",
				"adress_stadt" ].map(clientConfig).filter(Boolean);
		const contact = [ "telefon", "fax", "mail", "web" ]
			.map(clientConfig).filter(Boolean);
		writer.ensureBelow(115 - (address.length - 1) * 15);
		for (const line of address) { writer.line(line, { bold: true }); }
		writer.ensureBelow(180 - (contact.length - 1) * 15);
		for (const line of contact) { writer.line(line); }
		const logo = await embedLogo(context, clientConfig("logo"));
		if (logo) { writer.drawImage(logo, 357, 50, 168, 156); }
		writer.ensureBelow(200);
	}

	writer.heading("Kinderakte — " + info.name);
	addMetaLine(writer, "Gruppe", info.groupName);
	addMetaLine(writer, "Geboren am", info.birthday);
	addMetaLine(writer, "Hilfebeginn", info.helpBegin);
	addMetaLine(writer, "Zeitraum", formatRange(from, to));
	addMetaLine(writer, "Kategorien",
		categoryNames && categoryNames.length > 0
			? categoryNames.join(", ") : "Alle");
	addMetaLine(writer, "Anzahl Dokumente", clientFiles.length);
	addMetaLine(writer, "Exportiert", formatDate(new Date()) +
		(exportedBy ? ", " + exportedBy : ""));
}

function addMetaLine(writer, label, value) {
	if (value === undefined || value === null || value === "") { return; }
	writer.row([
		{ text: label, x: 0, width: 115 },
		{ text: String(value), x: 120, width: 335 },
	]);
}

// Column geometry of the document index (x relative to the left
// margin; the text area is 455pt wide).
const INDEX_COLUMNS = {
	title    : { x: 0,   width: 245 },
	date     : { x: 255, width: 70  },
	category : { x: 335, width: 120 },
};

function addDocumentIndex(writer, results) {
	writer.space();
	writer.heading("Dokumente");
	writer.row([
		{ text: "Titel", ...INDEX_COLUMNS.title },
		{ text: "Datum", ...INDEX_COLUMNS.date },
		{ text: "Kategorie", ...INDEX_COLUMNS.category },
	], { bold: true });
	for (const result of results) {
		const document = result.document;
		writer.row([
			{ text: document.title, ...INDEX_COLUMNS.title },
			{ text: document.date, ...INDEX_COLUMNS.date },
			{ text: document.category, ...INDEX_COLUMNS.category },
		]);
		if (!result.source) {
			writer.line("konnte nicht geladen werden", { indent: 10 });
		}
	}
}

function addCoverSheet(writer, document) {
	writer.newPage();
	writer.heading(document.title);
	addMetaLine(writer, "Kategorie", document.category);
	addMetaLine(writer, "Erstellt von", document.creator);
	addMetaLine(writer, "Datum", document.date);
	addMetaLine(writer, "Typ", document.documentType);
	addMetaLine(writer, "Dateiname", document.fileName);
}
